import { memo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Form, Input, Row, Space } from 'antd';
import { map } from 'lodash';
import {
  useGetAdoptionLiaisonsChildQuery,
  useGetChildStatusTypesQuery,
  useGetCountiesQuery,
  useGetCriteriaTypesQuery,
} from '@/services/general-api';
import { useSearchChildrenMutation } from '@/services/child-api';
import { BanSvg, SearchSvg } from '@/assets/icons';
import CollapsePanel from '@/components/collapse-panel';
import Select from '@/components/select';
import Button from '@/components/button';

const SearchPanel = ({ params }) => {
  const navigate = useNavigate();
  const { data: criteriaData, isLoading: loadingCriteriaData } = useGetCriteriaTypesQuery();
  const { data: liaisonData, isLoading: loadingLiaisonData } = useGetAdoptionLiaisonsChildQuery();
  const { data: countryData, isLoading: loadingCountryData } = useGetCountiesQuery();
  const { data: statusData, isLoading: loadingStatusData } = useGetChildStatusTypesQuery();
  const [searchChildren, { isLoading: loadingSearch }] = useSearchChildrenMutation();
  const [form] = Form.useForm();
  const refData = useRef({});

  useEffect(() => {
    if (params) {
      form.setFieldsValue({
        ...params,
        adoptionLiaisonsIds: map(params.adoptionLiaisonsIds, 'id'),
        countyIds: map(params.countyIds, 'id'),
        statusIds: map(params.statusIds, 'id'),
      });
    } else {
      form.setFieldsValue({
        statusIds: statusData?.filter((item) => item.name !== 'All').map((item) => item.id),
      });
    }
  }, [form, params, statusData]);

  useEffect(() => {
    if (params && refData.current.submitted) {
      refData.current.submitted = false;
      document.getElementById('search-results').scrollIntoView({ behavior: 'smooth' });
    }
  }, [params]);

  const onFinish = async (values) => {
    values.adoptionLiaisonsIds = values.adoptionLiaisonsIds?.map((id) => ({ id }));
    values.countyIds = values.countyIds?.map((id) => ({ id }));
    values.statusIds = values.statusIds?.map((id) => ({ id }));

    try {
      const searchId = await searchChildren(values).unwrap();
      navigate(`.?q=${searchId}`);
      refData.current.submitted = true;
    } catch (err) {}
  };

  const onReset = () => {
    form.resetFields();
    navigate('.');
  };

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={{
        name: {
          first: { criteria: 'Contains' },
          last: { criteria: 'Contains' },
        },
      }}
      onFinish={onFinish}
    >
      <CollapsePanel icon={<SearchSvg />} title="Children Search">
        <Row gutter={24}>
          <Col xs={24} md={12} xl={8}>
            <Form.Item label="First Name">
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name={['name', 'first', 'criteria']} noStyle>
                  <Select
                    valueName="name"
                    options={criteriaData}
                    loading={loadingCriteriaData}
                    style={{ maxWidth: 125 }}
                  />
                </Form.Item>
                <Form.Item name={['name', 'first', 'value']} noStyle>
                  <Input />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Form.Item label="Last Name">
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name={['name', 'last', 'criteria']} noStyle>
                  <Select
                    valueName="name"
                    options={criteriaData}
                    loading={loadingCriteriaData}
                    style={{ maxWidth: 125 }}
                  />
                </Form.Item>
                <Form.Item name={['name', 'last', 'value']} noStyle>
                  <Input />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Form.Item name="childCode" label="Child ID">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Form.Item name="adoptionLiaisonsIds" label="Adoption Liaison">
              <Select mode="multiple" options={liaisonData} loading={loadingLiaisonData} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Form.Item name="countyIds" label="County">
              <Select mode="multiple" options={countryData} loading={loadingCountryData} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Form.Item name="statusIds" label="Status">
              <Select mode="multiple" options={statusData} loading={loadingStatusData} />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ display: 'flex', justifyContent: 'end', gap: 16 }}>
          <Button icon={<BanSvg />} onClick={onReset} disabled={loadingSearch}>
            Reset
          </Button>
          <Button type="primary" htmlType="submit" icon={<SearchSvg />} loading={loadingSearch}>
            Search
          </Button>
        </div>
      </CollapsePanel>
    </Form>
  );
};

export default memo(SearchPanel);
